/**
 * Where task dots and lines sit on a video frame or image.
 *
 * Tasks created before this module stored their geometry relative to the player box: video points as a
 * percentage of the box (letterbox bars included, truncated to whole percents by the backend), image points
 * in image pixels, and drawn lines in pixels of the box as it was at drawing time, with no reference size.
 * So they moved off their spot whenever the player had a different size or shape.
 *
 * New tasks store a versioned object in the task's free-form `coords` field instead: the point and the line
 * as fractions (0–1) of the picture itself. Those stay on the same spot of the picture at any player size.
 * Old tasks are mapped onto the picture as well as their data allows (see `taskShape`).
 */

export type Point = [number, number];

export interface Size {
  width: number;
  height: number;
}

export interface Rect extends Size {
  left: number;
  top: number;
}

/** How the picture sits in the annotation box, in box pixels. */
export interface MediaLayout {
  /** The annotation box: the player container, or the image container */
  box: Size;
  /** Where the picture is drawn inside the box, without letterbox bars */
  media: Rect;
  /** Intrinsic size of the picture: video frame or image pixels */
  natural: Size;
}

/** Geometry stored in `coords` by tasks created with this module: fractions of the picture. */
export interface TaskGeometry {
  v: 2;
  pt: Point;
  line?: Point[];
}

/** Anything the task store may hold in `coords`: a geometry object, an old pixel line, or nothing. */
export type StoredCoords = TaskGeometry | Point[] | false | null | undefined;

interface TaskLike {
  coord_x?: string | number;
  coordX?: string | number;
  coord_y?: string | number;
  coordY?: string | number;
  coords?: unknown;
}

const isPoint = (p: unknown): p is Point =>
  Array.isArray(p) && p.length >= 2 && Number.isFinite(p[0]) && Number.isFinite(p[1]);

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));
const round4 = (n: number) => Math.round(n * 10000) / 10000;

/** The rect a picture of `media` size fills inside `box` with `object-fit: contain`. */
export function containRect(box: Size, media: Size): Rect {
  const scale = Math.min(box.width / media.width, box.height / media.height);
  const width = media.width * scale;
  const height = media.height * scale;
  return { left: (box.width - width) / 2, top: (box.height - height) / 2, width, height };
}

/** The whole box as the picture: used until the picture's size is known. */
export function boxLayout(box: Size): MediaLayout {
  return { box, media: { left: 0, top: 0, ...box }, natural: box };
}

export function readGeometry(coords: unknown): TaskGeometry | null {
  if (!coords || typeof coords !== 'object' || Array.isArray(coords)) return null;
  const g = coords as Partial<TaskGeometry>;
  if (g.v !== 2 || !isPoint(g.pt)) return null;
  return { v: 2, pt: g.pt, line: Array.isArray(g.line) ? g.line.filter(isPoint) : undefined };
}

/** Box pixels → fraction of the picture, clamped to its edges. */
export function toMedia([x, y]: Point, { media }: MediaLayout): Point {
  return [round4(clamp01((x - media.left) / media.width)), round4(clamp01((y - media.top) / media.height))];
}

/** Fraction of the picture → box pixels. */
export function fromMedia([fx, fy]: Point, { media }: MediaLayout): Point {
  return [media.left + fx * media.width, media.top + fy * media.height];
}

/** Moves a box-pixel point onto the picture, so clicks on the letterbox bars land on its edge. */
export function clampToMedia(p: Point, layout: MediaLayout): Point {
  return fromMedia(toMedia(p, layout), layout);
}

/** Geometry to store for a new task, from box-pixel input. */
export function makeGeometry(pt: Point, line: Point[] | null | undefined, layout: MediaLayout): TaskGeometry {
  const geometry: TaskGeometry = { v: 2, pt: toMedia(pt, layout) };
  if (line && line.length > 1) geometry.line = line.map((p) => toMedia(p, layout));
  return geometry;
}

export function toPercent([x, y]: Point, { box }: MediaLayout): { x: number; y: number } {
  return { x: (x / box.width) * 100, y: (y / box.height) * 100 };
}

/**
 * A size estimated from an old line task is only used if its point is far enough from the edge for the
 * estimate to be stable (video points are whole percents), and if the result is a plausible player size.
 */
const MIN_FRACTION = 0.03;
const plausible = (s: Size) => s.width >= 100 && s.width <= 10000 && s.height >= 100 && s.height <= 10000;

/**
 * Where to draw a task, in box pixels: its point, and its line for line tasks.
 *
 * - New tasks: from their stored fractions of the picture.
 * - Old image tasks: the point is in image pixels, so it maps exactly. The line was stored in pixels of the
 *   image as displayed then; its first point is the task point, which gives the display size then.
 * - Old video line tasks: the first line point and the stored box percentage give the box size at drawing
 *   time, and with it where the picture was. Both point and line are then mapped onto the picture.
 * - Old video point tasks: nothing records the box size, so they stay box-relative as before.
 */
export function taskShape(
  task: TaskLike,
  layout: MediaLayout,
  mediaType: 'video' | 'image',
): { pt: Point | null; line: Point[] | null } {
  const geometry = readGeometry(task.coords);
  if (geometry) {
    return {
      pt: fromMedia(geometry.pt, layout),
      line: geometry.line && geometry.line.length > 1 ? geometry.line.map((p) => fromMedia(p, layout)) : null,
    };
  }

  const cx = Number(task.coord_x ?? task.coordX);
  const cy = Number(task.coord_y ?? task.coordY);
  if (!Number.isFinite(cx) || !Number.isFinite(cy)) return { pt: null, line: null };
  const oldLine = Array.isArray(task.coords) ? task.coords.filter(isPoint) : [];
  const hasLine = oldLine.length > 1;

  if (mediaType === 'image') {
    const f: Point = [cx / layout.natural.width, cy / layout.natural.height];
    const pt = fromMedia(f, layout);
    if (!hasLine) return { pt, line: null };
    const [x0, y0] = oldLine[0];
    const shown = { width: x0 / f[0], height: y0 / f[1] };
    if (f[0] < MIN_FRACTION || f[1] < MIN_FRACTION || !plausible(shown)) return { pt, line: oldLine };
    return { pt, line: oldLine.map(([x, y]) => fromMedia([x / shown.width, y / shown.height], layout)) };
  }

  const boxPoint: Point = [(cx / 100) * layout.box.width, (cy / 100) * layout.box.height];
  if (!hasLine) return { pt: boxPoint, line: null };

  // The backend truncates the stored percentage, so its middle (+0.5) is the best estimate
  const [x0, y0] = oldLine[0];
  const fx = (cx + 0.5) / 100;
  const fy = (cy + 0.5) / 100;
  const box = { width: x0 / fx, height: y0 / fy };
  if (fx < MIN_FRACTION || fy < MIN_FRACTION || !plausible(box)) return { pt: boxPoint, line: oldLine };
  const then: MediaLayout = { box, media: containRect(box, layout.natural), natural: layout.natural };
  return {
    pt: fromMedia(toMedia(oldLine[0], then), layout),
    line: oldLine.map((p) => fromMedia(toMedia(p, then), layout)),
  };
}

/** Where a task's popover should anchor, as box percentages. */
export function taskAnchor(task: TaskLike, layout: MediaLayout, mediaType: 'video' | 'image') {
  const { pt } = taskShape(task, layout, mediaType);
  return pt ? toPercent(pt, layout) : null;
}
